import { AxiosResponse } from 'axios';
import _ from 'lodash';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';

/**
 * Interface representing a status message as returned from the API.
 * Note: This uses the API's field naming convention (englishText, germanText, active).
 * Functions in this file accept parameters with different names (textEn, textDe, isActive)
 * but convert them to the API format before sending requests.
 */
export interface StatusMessage {
  id: string;
  englishText: string;
  germanText: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get all active status messages
 * @returns Promise with active status messages
 */
export const getActiveStatusMessages = async (): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<StatusMessage[]>('/misc/status-messages');
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while fetching active status messages');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Get all status messages (admin view)
 * @returns Promise with all status messages
 */
export const getAllStatusMessages = async (): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<StatusMessage[]>('/statusMessage');
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while fetching all status messages');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Get a single status message by ID
 * @param id Status message ID
 * @returns Promise with status message
 */
export const getStatusMessage = async (id: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get<StatusMessage>(`/statusMessage/${id}`);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while fetching status message');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Create a new status message
 * @param statusMessage Status message properties
 * @returns Promise with created status message
 */
export const createStatusMessage = async (statusMessage: {
  textEn: string;
  textDe: string;
  isActive: boolean;
}): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const payload = {
      englishText: statusMessage.textEn,
      germanText: statusMessage.textDe,
      active: statusMessage.isActive,
    };

    const response = await API_V1.post<StatusMessage>('/statusMessage', payload);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while creating status message');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Update an existing status message
 * @param id Status message ID
 * @param statusMessage Status message fields to update
 * @returns Promise with updated status message
 */
export const updateStatusMessage = async (
  id: string,
  statusMessage: Partial<{
    textEn: string;
    textDe: string;
    isActive: boolean;
  }>
): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const payload: any = {};

    if (statusMessage.textEn !== undefined) {
      payload.englishText = statusMessage.textEn;
    }

    if (statusMessage.textDe !== undefined) {
      payload.germanText = statusMessage.textDe;
    }

    if (statusMessage.isActive !== undefined) {
      payload.active = statusMessage.isActive;
    }

    const response = await API_V1.put<StatusMessage>(`/statusMessage/${id}`, payload);
    return { data: response.data };
  } catch (e) {
    Logger.trace('Error while updating status message');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

/**
 * Delete a status message
 * @param id Status message ID
 * @returns Promise with success indicator
 */
export const deleteStatusMessage = async (id: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.delete(`/statusMessage/${id}`);
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting status message');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
